import { PointCloudObject, useCached3DObjectsIfExists } from "@/object-cache";
import {
  selectActiveAnalysisId,
  selectAnalysisAndPointCloud,
  selectShowActiveAnalysis,
} from "@/store/point-cloud-analysis-tool-selector";
import { PointCloudAnalysis } from "@/store/point-cloud-analysis-tool-slice";
import { useAppSelector } from "@/store/store-hooks";
import { assert } from "@faro-lotv/foundation";
import { isIElementGenericPointCloudStream } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { useMemo, type JSX } from "react";
import { AnalysisDockingToolbar } from "./analysis-docking-toolbar";

/**
 * @returns The optional AnalysisDockingToolbar component if necessary
 */
export function AnalysisToolBarTool(): JSX.Element | null {
  const { activePointCloud, activeAnalysis } = useAnalysisToolBar();

  const showActiveAnalysis = useAppSelector(selectShowActiveAnalysis);

  if (
    activePointCloud !== undefined &&
    activeAnalysis !== undefined &&
    showActiveAnalysis
  ) {
    return (
      <AnalysisDockingToolbar
        pointCloud={activePointCloud}
        analysis={activeAnalysis}
        // Using key props to force re-rendering of the component
        // when the active analysis changes
        key={activeAnalysis.id}
      />
    );
  }
  return null;
}

type AnalysisToolBarReturnType = {
  activePointCloud?: PointCloudObject;
  activeAnalysis?: PointCloudAnalysis;
};
/**
 * @returns The optional active point cloud and the optional visible active analysis
 */
function useAnalysisToolBar(): AnalysisToolBarReturnType {
  const activeAnalysisId = useAppSelector(selectActiveAnalysisId);

  const { pointCloudId, activeAnalysis } = useAppSelector(
    selectAnalysisAndPointCloud(activeAnalysisId),
  );

  const activePointCloud = useAppSelector(selectIElement(pointCloudId));

  assert(
    activePointCloud === undefined ||
      isIElementGenericPointCloudStream(activePointCloud),
    "Expected the analysis parent to be a point cloud",
  );

  const elementsToRequest = useMemo(
    () => (activePointCloud ? [activePointCloud] : []),
    [activePointCloud],
  );

  const pointCloud = useCached3DObjectsIfExists(
    elementsToRequest,
    "PointCloudAnalysisToolbar",
  );

  const activeItems = useMemo(
    (): AnalysisToolBarReturnType => ({
      activePointCloud: pointCloud[0],
      activeAnalysis,
    }),
    [pointCloud, activeAnalysis],
  );

  return activeItems;
}
